import type { RuleSet } from 'styled-components';

import { useMemo } from 'react';
import styled, { css } from 'styled-components';

import { baseTextStyle } from './styles';
import { TilePriceConfig } from './TilePriceConfig';
import { TileTitleOriginalPrice } from './TileTitleOriginalPrice';
import { TileTitlePrice } from './TileTitlePrice';
import { media } from '~/components/shared/core/styles';
import { useTileBaseContext } from '~/components/shared/recommendations/tile/TileBaseContext';
import { useTileContext } from '~/components/shared/recommendations/tile/TileContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { LowestPriceDisplay, SiteType } from '~/utilities/graphql/codegen';
import { localisedData } from '~/utilities/helpers';
import { useMembersOnlyPrices } from '~/utilities/hooks/useMembersOnlyPrices';

const styles = {
  GStarWrapper: css`
    flex-direction: column;

    @media ${({ theme }) => media(theme).greaterThan('lg')} {
      gap: ${({ theme }) => theme.size['0-5']};
    }
  `,
  AafWrapper: css`
    flex-direction: column;
  `,
  WahtsWrapper: css`
    flex-direction: column;

    @media ${({ theme }) => media(theme).greaterThan('sm')} {
      gap: ${({ theme }) => theme.size['0-5']};
    }
  `,

  GStarTitle: css`
    ${baseTextStyle};
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 1.4;

    @media ${({ theme }) => media(theme).lessThan('lg')} {
      font-size: ${({ theme }) => theme.font.size[2]};
    }
  `,
  AafTitle: css`
    font-weight: 700;
    line-height: 1.5;
    text-transform: uppercase;
    font-size: ${({ theme }) => theme.font.size[2]};
  `,
  WahtsTitle: css`
    ${baseTextStyle};
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 1.4;
    font-weight: ${({ theme }) => theme.variables.font.weight.bold};
    color: ${({ theme }) => theme.colors.content.primary};

    @media ${({ theme }) => media(theme).lessThan('lg')} {
      font-size: ${({ theme }) => theme.font.size[3]};
    }
  `,
  WahtsInitialPrice: css`
    white-space: nowrap;
    font-weight: ${({ theme }) => theme.variables.font.weight.medium};
    color: ${({ theme }) => theme.colors.content.primary};

    @media ${({ theme }) => media(theme).lessThan('sm')} {
      font-size: ${({ theme }) => theme.font.size[3]};
    }
  `,

  GStarInitialPrice: css`
    white-space: nowrap;
    font-weight: ${({ theme }) => theme.variables.font.weight.medium};

    @media ${({ theme }) => media(theme).lessThan('lg')} {
      font-size: ${({ theme }) => theme.font.size[2]};
    }
  `,
  AafInitialPrice: css`
    line-height: 16px;
    white-space: nowrap;
    font-size: ${({ theme }) => theme.font.size[2]};
  `,

  GStarPriceWrapper: css`
    flex-wrap: wrap;
  `,
  AafPriceWrapper: css``,

  GStarPriceLowest30Days: css`
    @media ${({ theme }) => media(theme).lessThan('lg')} {
      font-size: ${({ theme }) => theme.font.size[2]};
    }
  `,
  WahtsPriceLowest30Days: css`
    @media ${({ theme }) => media(theme).lessThan('sm')} {
      font-size: ${({ theme }) => theme.font.size[3]};
    }
  `,
  AafPriceLowest30Days: css`
    font-size: ${({ theme }) => theme.font.size[2]};
    font-weight: ${({ theme }) => theme.variables.font.weight.regular};
  `,

  GStarDiscountedPrice: css`
    @media ${({ theme }) => media(theme).lessThan('lg')} {
      font-size: ${({ theme }) => theme.font.size[2]};
    }
  `,
  WahtsDiscountedPrice: css`
    @media ${({ theme }) => media(theme).lessThan('sm')} {
      font-size: ${({ theme }) => theme.font.size[3]};
    }
  `,
  AafDiscountedPrice: css`
    font-size: ${({ theme }) => theme.font.size[2]};
  `,
};

const WrapperStyles: Record<SiteType, RuleSet<object>> = {
  [SiteType.Gstar]: styles.GStarWrapper,
  [SiteType.Outlet]: styles.GStarWrapper,
  [SiteType.Employeeshop]: styles.GStarWrapper,
  [SiteType.Aaf]: styles.AafWrapper,
  [SiteType.Wahts]: styles.WahtsWrapper,
};

const TitleStyles: Record<SiteType, RuleSet<object>> = {
  [SiteType.Gstar]: styles.GStarTitle,
  [SiteType.Outlet]: styles.GStarTitle,
  [SiteType.Employeeshop]: styles.GStarTitle,
  [SiteType.Aaf]: styles.AafTitle,
  [SiteType.Wahts]: styles.WahtsTitle,
};

const InitialPriceStyles: Record<SiteType, RuleSet<object>> = {
  [SiteType.Gstar]: styles.GStarInitialPrice,
  [SiteType.Outlet]: styles.GStarInitialPrice,
  [SiteType.Employeeshop]: styles.GStarInitialPrice,
  [SiteType.Aaf]: styles.AafInitialPrice,
  [SiteType.Wahts]: styles.WahtsInitialPrice,
};

const PriceWrapperStyles: Record<SiteType, RuleSet<object>> = {
  [SiteType.Gstar]: styles.GStarPriceWrapper,
  [SiteType.Outlet]: styles.GStarPriceWrapper,
  [SiteType.Employeeshop]: styles.GStarPriceWrapper,
  [SiteType.Aaf]: styles.AafPriceWrapper,
  [SiteType.Wahts]: styles.GStarPriceWrapper,
};

const PriceLowest30DaysStyles: Record<SiteType, RuleSet<object>> = {
  [SiteType.Gstar]: styles.GStarPriceLowest30Days,
  [SiteType.Outlet]: styles.GStarPriceLowest30Days,
  [SiteType.Employeeshop]: styles.GStarPriceLowest30Days,
  [SiteType.Aaf]: styles.AafPriceLowest30Days,
  [SiteType.Wahts]: styles.WahtsPriceLowest30Days,
};

const DiscountedPriceStyles: Record<SiteType, RuleSet<object>> = {
  [SiteType.Gstar]: styles.GStarDiscountedPrice,
  [SiteType.Outlet]: styles.GStarDiscountedPrice,
  [SiteType.Employeeshop]: styles.GStarDiscountedPrice,
  [SiteType.Aaf]: styles.AafDiscountedPrice,
  [SiteType.Wahts]: styles.WahtsDiscountedPrice,
};

export const S = {
  Wrapper: styled.div`
    ${baseTextStyle};
    display: flex;
    color: ${({ theme }) => theme.colors.content.primary};
    ${({ theme }) => WrapperStyles[theme.siteType]}
  `,

  PriceWrapper: styled.div`
    display: flex;
    ${({ theme }) => PriceWrapperStyles[theme.siteType]}
  `,

  PriceLowest30Days: styled.span`
    ${baseTextStyle};
    font-weight: ${({ theme }) => theme.variables.font.weight.medium};
    color: ${({ theme }) => theme.colors.content.tertiary};
    margin-right: ${({ theme }) => theme.padding['3xs']};
    text-decoration: line-through;
    white-space: nowrap;

    ${({ theme }) => PriceLowest30DaysStyles[theme.siteType]}

    /* CONFIG price block (TilePriceConfig): strikethrough driven by the site configuration */
    &[data-strikethrough='false'] {
      text-decoration: none;
    }
  `,

  PriceTitleOriginal: styled.div`
    word-break: break-word;
  `,

  Title: styled.p`
    ${({ theme }) => TitleStyles[theme.siteType]}
  `,

  DiscountedPrice: styled.span<{ $color?: string | null }>`
    ${baseTextStyle};
    white-space: nowrap;
    font-weight: ${({ theme }) => theme.variables.font.weight.bold};
    color: ${({ $color, theme }) => $color || theme.colors.content.sale};

    ${({ theme }) => DiscountedPriceStyles[theme.siteType]}
  `,

  InitialPrice: styled.span`
    ${({ theme }) => theme.colors.content.primary}
    ${({ theme }) => InitialPriceStyles[theme.siteType]}
  `,
};

interface TileTitleWithPriceProps {
  className?: string;
  renderLowestPrice?: boolean | null;
  discountedColor?: string;
}

export const TileTitleWithPrice = ({
  className,
  renderLowestPrice,
  discountedColor,
}: TileTitleWithPriceProps) => {
  const { lang } = useAppContext();
  const {
    priceFormatter,
    configuration: { lowestPriceDisplay },
  } = useStaticContext();
  const { singleProduct: product } = useTileBaseContext();
  const { hideElements, productPrice } = useTileContext();

  const [name, initialPrice, originalPriceCopy] = useMemo(
    () => [
      localisedData(product, product?.name, lang),
      product?.fromPriceValue ? priceFormatter(product.fromPriceValue) : undefined,
      productPrice?.originalPrice as string,
    ],
    [product, lang, priceFormatter, productPrice]
  );

  const { priceFormatted } = useMembersOnlyPrices({
    price: product.priceValue,
    personalisedPrice: product.personalisedPriceValue,
  });

  const isConfigPriceDisplay = lowestPriceDisplay === LowestPriceDisplay.Config;

  return (
    <S.Wrapper>
      {hideElements?.name !== true && (
        <S.Title className={className} data-testid="product-tile-title">
          {name}
        </S.Title>
      )}
      {hideElements?.price !== true && isConfigPriceDisplay && (
        <TilePriceConfig discountedColor={discountedColor} />
      )}
      {hideElements?.price !== true &&
        !isConfigPriceDisplay &&
        (renderLowestPrice ? (
          originalPriceCopy && (
            <TileTitleOriginalPrice
              discountedPrice={priceFormatted} // pass price formatted
              initialPrice={initialPrice}
              originalPriceCopy={originalPriceCopy}
              discountedColor={discountedColor}
            />
          )
        ) : (
          <TileTitlePrice
            discountedPrice={priceFormatted} // pass price formatted
            initialPrice={initialPrice}
            originalPriceCopy={originalPriceCopy}
            discountedColor={discountedColor}
          />
        ))}
    </S.Wrapper>
  );
};
